import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../hooks/useApi';
import * as sanitizeHtml from 'sanitize-html';
import Modal from '../components/Modal';
import { FaEye } from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const EducationalContentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: content, isLoading, error } = useApi(`/content/${id}`);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (error) {
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in.');
        navigate('/login');
      } else {
        toast.error(error.message || 'Failed to load content');
      }
    }
  }, [error, navigate, id]);

  const handleImageClick = (url) => {
    if (url && url.match(/\.(jpg|jpeg|png)$/i)) {
      setSelectedImage(`${BASE_URL}${url}`);
      setIsImageModalOpen(true);
    }
  };

  const handleImageModalClose = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <p className="text-center text-red-600 text-lg font-medium">Content not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-primary tracking-tight sm:text-5xl">{content.title}</h1>
          <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
            Dive into detailed insights and resources for {content.title}.
          </p>
          <nav className="mt-4 text-sm text-gray-500">
            <a href="/" className="hover:text-secondary transition-colors">Home</a>
            <span className="mx-2">/</span>
            <a href="/content" className="hover:text-secondary transition-colors">Educational Content</a>
            <span className="mx-2">/</span>
            <span className="text-secondary">{content.title}</span>
          </nav>
        </header>

        <div className="bg-white rounded-xl shadow-md p-6 max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column */}
            <div className="w-full lg:w-1/2 order-1">
              <div className="relative group">
                <img
                  src={content.previewImage?.url ? `${BASE_URL}${content.previewImage.url}` : 'https://via.placeholder.com/400x250'}
                  alt={content.title}
                  className="w-full h-64 object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]"
                  onClick={() => handleImageClick(content.previewImage?.url)}
                  loading="lazy"
                  role="button"
                  aria-label={`View full-size preview image for ${content.title}`}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black bg-opacity-30 rounded-lg">
                  <FaEye className="text-white text-2xl" />
                </div>
              </div>
              <div className="mt-6 space-y-3 text-gray-600 text-sm sm:text-base">
                <p><span className="font-medium">Category:</span> {content.category?.name || 'N/A'}</p>
                <p><span className="font-medium">Subcategory:</span> {content.subcategory || 'N/A'}</p>
                <p><span className="font-medium">Type:</span> {content.type || 'N/A'}</p>
                {content.contentUrl && (
                  <p>
                    <span className="font-medium">Content URL:</span>{' '}
                    <a
                      href={content.contentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:underline"
                      aria-label={`Visit external content link for ${content.title}`}
                    >
                      {content.contentUrl}
                    </a>
                  </p>
                )}
                <p><span className="font-medium">Views:</span> {content.engagement?.views || 0}</p>
                <p><span className="font-medium">Likes:</span> {content.engagement?.likes || 0}</p>
              </div>
            </div>
            {/* Right Column */}
            <div className="w-full lg:w-1/2 order-2">
              <h3 className="text-xl font-semibold text-primary mb-4">Description</h3>
              <div
                className="text-gray-600 text-sm sm:text-base mb-6 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml.default(content.description || 'No description provided.', {
                    allowedTags: ['p', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'img'],
                    allowedAttributes: { a: ['href'], img: ['src', 'alt'] },
                  }),
                }}
              />
              {content.files?.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-4">Resources</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {content.files.map((file, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        {file.type === 'image' ? (
                          <div className="relative group">
                            <img
                              src={`${BASE_URL}${file.url}`}
                              alt={`File ${idx + 1}`}
                              className="w-40 h-40 object-cover rounded-lg transition-transform duration-300 group-hover:scale-[1.02]"
                              onClick={() => handleImageClick(file.url)}
                              loading="lazy"
                              role="button"
                              aria-label={`View full-size file ${idx + 1} for ${content.title}`}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black bg-opacity-30 rounded-lg">
                              <FaEye className="text-white text-xl" />
                            </div>
                          </div>
                        ) : (
                          <a
                            href={`${BASE_URL}${file.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-4 py-2 bg-secondary text-white rounded-md hover:bg-opacity-90 text-sm font-medium transition duration-300"
                            aria-label={`Download PDF file ${idx + 1} for ${content.title}`}
                          >
                            Download File {idx + 1} (PDF)
                          </a>
                        )}
                        <p className="text-gray-600 text-sm mt-2">File {idx + 1}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-8 flex justify-end">
            <button
              onClick={() => navigate('/content')}
              className="px-6 py-2 bg-secondary text-white font-semibold rounded-md hover:bg-opacity-90 transition duration-300"
              aria-label="Back to educational content list"
            >
              Back to Content
            </button>
          </div>
        </div>

        {/* Image Modal */}
        <Modal
          isOpen={isImageModalOpen}
          onClose={handleImageModalClose}
          title="View Image"
          className="bg-white p-6 max-w-5xl mx-auto rounded-xl shadow-lg"
        >
          <div className="flex justify-center items-center">
            <img
              src={selectedImage}
              alt="Full-size attachment"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>
          <button
            onClick={handleImageModalClose}
            className="mt-4 w-full bg-secondary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition duration-300 font-semibold text-sm sm:text-base"
            aria-label="Close image modal"
          >
            Close
          </button>
        </Modal>
      </div>
    </div>
  );
};

export default EducationalContentDetails;